/**
 * LiveAnalytics — Phase 4 dashboard for creators.
 * Renders inside the "Analytics" tab of the Webinar Detail page.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip,
  BarChart, Bar, CartesianGrid,
} from "recharts";
import {
  Users, TrendingUp, Clock, Percent, MessageSquare,
  Smile, HelpCircle, BarChart3, Hand, PlayCircle,
} from "lucide-react";

const fmtDuration = (s) => {
  if (!s || s < 0) return "0s";
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return h ? `${h}h ${m}m` : m ? `${m}m ${sec}s` : `${sec}s`;
};

export default function LiveAnalytics({ webinarId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get(`/webinars/${webinarId}/live/analytics`);
      setData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  };
  useEffect(() => {
    load();
    if (!autoRefresh) return;
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [webinarId, autoRefresh]);

  if (loading) return <div className="text-[#4A4D57] py-6">Loading analytics…</div>;
  if (!data) return <div className="text-[#4A4D57] py-6">No data yet.</div>;

  const s = data.summary;
  return (
    <div className="space-y-6" data-testid="live-analytics-panel">
      <div className="flex items-center justify-between">
        <h2 className="font-display font-black text-xl">Live Session Analytics</h2>
        <label className="text-xs text-[#747680] inline-flex items-center gap-1.5">
          <input
            type="checkbox"
            checked={autoRefresh}
            onChange={(e) => setAutoRefresh(e.target.checked)}
            data-testid="analytics-auto-refresh"
          />
          Auto-refresh (10s)
        </label>
      </div>

      {/* KPI grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <KPI icon={Users} label="Live now" value={s.active_now} color="#DC2626" testid="kpi-active-now" pulse={s.active_now > 0} />
        <KPI icon={TrendingUp} label="Peak" value={s.peak_attendees} color="#059669" testid="kpi-peak" />
        <KPI icon={Users} label="Unique" value={s.total_unique_attendees} color="#0B5CFF" testid="kpi-unique" />
        <KPI icon={Percent} label="Attend rate" value={`${s.attendance_rate_pct}%`} color="#7C3AED" testid="kpi-rate" />
        <KPI icon={Clock} label="Avg watch" value={fmtDuration(s.avg_watch_seconds)} color="#B45309" testid="kpi-avg-watch" />
        <KPI icon={PlayCircle} label="Duration" value={fmtDuration(s.session_length_seconds)} color="#0B0D17" testid="kpi-duration" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Attendance time-series */}
        <div className="vp-card p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-display font-semibold text-sm">Attendance over time</h3>
            <span className="text-xs text-[#747680]">{data.timeseries.length} min</span>
          </div>
          <div style={{ width: "100%", height: 220 }}>
            {data.timeseries.length ? (
              <ResponsiveContainer>
                <LineChart data={data.timeseries} margin={{ top: 5, right: 8, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E4E6EC" />
                  <XAxis dataKey="t" tick={{ fontSize: 10 }} label={{ value: "min", position: "insideBottom", offset: -2, fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                  <Tooltip contentStyle={{ fontSize: 12 }} />
                  <Line type="monotone" dataKey="attendees" stroke="#0B5CFF" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <EmptyChart label="No live session yet" />
            )}
          </div>
        </div>

        {/* Drop-off */}
        <div className="vp-card p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-display font-semibold text-sm">Watch-time drop-off</h3>
            <span className="text-xs text-[#747680]">% of session watched</span>
          </div>
          <div style={{ width: "100%", height: 220 }}>
            {data.dropoff.some((d) => d.attendees > 0) ? (
              <ResponsiveContainer>
                <BarChart data={data.dropoff} margin={{ top: 5, right: 8, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E4E6EC" />
                  <XAxis dataKey="bucket" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                  <Tooltip contentStyle={{ fontSize: 12 }} />
                  <Bar dataKey="attendees" fill="#7C3AED" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <EmptyChart label="No attendance yet" />
            )}
          </div>
        </div>
      </div>

      {/* Engagement */}
      <div className="vp-card p-4">
        <h3 className="font-display font-semibold text-sm mb-3">Engagement</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <Metric icon={MessageSquare} label="Chat msgs" value={data.engagement.chat_messages} />
          <Metric icon={Smile} label="Reactions" value={data.engagement.reactions} />
          <Metric icon={HelpCircle} label="Questions" value={data.engagement.questions} />
          <Metric icon={BarChart3} label="Polls" value={data.engagement.polls_created} />
          <Metric icon={BarChart3} label="Poll votes" value={data.engagement.poll_votes} />
          <Metric icon={Hand} label="Raised hands" value={data.engagement.raised_hands} />
        </div>
      </div>

      {data.recording_url && (
        <div className="vp-card p-4 flex items-center gap-3">
          <PlayCircle className="w-8 h-8 text-[#DC2626]" />
          <div className="flex-1 min-w-0">
            <div className="font-display font-semibold text-sm">Recording available</div>
            <a href={data.recording_url} target="_blank" rel="noreferrer" className="text-xs text-[#0B5CFF] hover:underline break-all">{data.recording_url}</a>
          </div>
          <a href={data.recording_url} target="_blank" rel="noreferrer" className="vp-btn-primary text-sm py-2 px-3">Watch →</a>
        </div>
      )}
    </div>
  );
}

function KPI({ icon: Icon, label, value, color, testid, pulse }) {
  return (
    <div className="vp-card p-3" data-testid={testid}>
      <div className="flex items-center gap-2 mb-1">
        <div className={"w-6 h-6 rounded-md flex items-center justify-center " + (pulse ? "animate-pulse" : "")} style={{ background: color + "18" }}>
          <Icon className="w-3.5 h-3.5" style={{ color }} strokeWidth={2} />
        </div>
        <span className="text-[10px] uppercase tracking-widest text-[#747680]">{label}</span>
      </div>
      <div className="font-display font-black text-xl">{value}</div>
    </div>
  );
}

function Metric({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-2 p-2 rounded-lg bg-[#F1F3F7]">
      <Icon className="w-4 h-4 text-[#4A4D57]" strokeWidth={1.75} />
      <div>
        <div className="text-[10px] text-[#747680]">{label}</div>
        <div className="font-semibold text-sm">{value}</div>
      </div>
    </div>
  );
}

function EmptyChart({ label }) {
  return (
    <div className="w-full h-full flex items-center justify-center text-[#747680] text-xs">{label}</div>
  );
}
